import React, { useEffect } from 'react';
import { attorneys, paralegals } from '../data/attorneys';
import { Award, GraduationCap, Briefcase, Scale, CheckCircle } from 'lucide-react';

const AttorneysPage = ({ onGetStarted }) => {
  useEffect(() => {
    document.title = 'Our Attorneys | Federal IP Office - Experienced Legal Team';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Meet our experienced attorneys and paralegals at Federal IP Office. Our team brings decades of combined expertise in corporate law, IP, litigation, and immigration.');
    }
  }, []);

  return (
    <div className="pt-20" data-testid="attorneys-page">
      {/* Hero Section */}
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1694253988732-816ab6e60caa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBtZWV0aW5nJTIwcm9vbSUyMGxvbmclMjB0YWJsZSUyMGNoYWlycyUyMHByb2Zlc3Npb25hbCUyMGludGVyaW9yfGVufDB8fHx8MTc3NTUyMTM3NXww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/75"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="text-sm uppercase tracking-[0.2em] font-medium text-white/90 mb-4">
              Our Team
            </div>
            <h1 className="text-5xl lg:text-6xl tracking-tight leading-none text-white font-bold mb-6" data-testid="attorneys-heading">
              Meet Our Legal Experts
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Our team of distinguished attorneys and dedicated paralegals brings decades of combined experience to serve your legal needs with excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Attorneys Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 text-center">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">
              Our Attorneys
            </h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">
              Meet our experienced legal professionals who are dedicated to protecting your interests and achieving your goals.
            </p>
          </div>

          <div className="space-y-12">
            {attorneys.map((attorney) => (
              <div 
                key={attorney.id} 
                className="bg-[#F8F9FA] border-l-4 border-[#CA9A67] p-8 hover:shadow-lg transition-shadow" 
                data-testid={`attorney-detail-${attorney.id}`}
              >
                <div className="mb-6">
                  <h3 className="text-3xl text-[#2C3E50] font-bold mb-2">
                    {attorney.name}
                  </h3>
                  <div className="text-lg text-[#2C3E50]/70 font-medium mb-4">
                    {attorney.title}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                  <div>
                    <div className="flex items-start space-x-3 mb-4">
                      <Briefcase className="w-5 h-5 text-[#CA9A67] mt-1 flex-shrink-0" strokeWidth={1.5} />
                      <div>
                        <div className="font-semibold text-[#2E2E2E] mb-1">Experience</div>
                        <div className="text-[#2E2E2E]/70">{attorney.experience}</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 mb-4">
                      <GraduationCap className="w-5 h-5 text-[#CA9A67] mt-1 flex-shrink-0" strokeWidth={1.5} />
                      <div>
                        <div className="font-semibold text-[#2E2E2E] mb-1">Education</div>
                        <div className="text-[#2E2E2E]/70">{attorney.education}</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <Scale className="w-5 h-5 text-[#CA9A67] mt-1 flex-shrink-0" strokeWidth={1.5} />
                      <div>
                        <div className="font-semibold text-[#2E2E2E] mb-1">Bar Admissions</div>
                        <div className="text-[#2E2E2E]/70">{attorney.barAdmissions}</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-start space-x-3 mb-4">
                      <Award className="w-5 h-5 text-[#CA9A67] mt-1 flex-shrink-0" strokeWidth={1.5} />
                      <div>
                        <div className="font-semibold text-[#2E2E2E] mb-1">Specialization</div>
                        <div className="text-[#2E2E2E]/70">{attorney.specialization}</div>
                      </div>
                    </div>

                    <div className="font-semibold text-[#2E2E2E] mb-2">Practice Areas</div>
                    <div className="flex flex-wrap gap-2">
                      {attorney.practiceAreas.map((area, idx) => (
                        <span key={idx} className="bg-white px-3 py-1 text-sm text-[#2C3E50] border border-[#2C3E50]/20">
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="font-semibold text-[#2E2E2E] mb-3">Profile</div>
                  <p className="text-base leading-relaxed text-[#2E2E2E]/80">
                    {attorney.bio}
                  </p>
                </div>

                <div>
                  <div className="font-semibold text-[#2E2E2E] mb-3">Notable Achievements</div>
                  <ul className="space-y-2">
                    {attorney.notableAchievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="w-5 h-5 text-[#CA9A67] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        <span className="text-[#2E2E2E]/80">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Paralegals Section */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16 text-center">
            <h2 className="text-3xl lg:text-4xl tracking-tight text-[#2E2E2E] font-bold mb-4">
              Our Paralegals
            </h2>
            <p className="text-base leading-relaxed text-[#2E2E2E]/80 max-w-2xl mx-auto">
              Our skilled paralegals provide essential support, ensuring every detail of your case receives meticulous attention.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {paralegals.map((paralegal) => (
              <div 
                key={paralegal.id} 
                className="bg-white border border-[#2C3E50]/5 shadow-sm p-8 hover:border-[#2C3E50]/20 transition-all" 
                data-testid={`paralegal-${paralegal.id}`}
              >
                <h3 className="text-2xl text-[#2C3E50] font-bold mb-2">
                  {paralegal.name}
                </h3>
                <div className="text-base text-[#2C3E50]/70 font-medium mb-4">
                  {paralegal.title}
                </div>
                
                <div className="space-y-3 mb-4">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="w-4 h-4 text-[#CA9A67]" strokeWidth={1.5} />
                    <span className="text-sm text-[#2E2E2E]/70"><strong>Experience:</strong> {paralegal.experience}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Award className="w-4 h-4 text-[#CA9A67]" strokeWidth={1.5} />
                    <span className="text-sm text-[#2E2E2E]/70"><strong>Focus:</strong> {paralegal.specialization}</span>
                  </div>
                </div>
                
                <p className="text-sm text-[#2E2E2E]/70 leading-relaxed">
                  {paralegal.bio}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-24 md:py-32 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-[#2C3E50]/85"></div>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-3xl lg:text-4xl tracking-tight text-white font-bold mb-6">
            Ready to Work With Our Team?
          </h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">
            Contact us today to schedule a consultation with one of our experienced attorneys.
          </p>
          <button
            onClick={onGetStarted}
            className="bg-white text-[#2C3E50] hover:bg-white/90 transition-colors px-8 py-4 font-medium"
            data-testid="attorneys-cta-button"
          >
            Get Started Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default AttorneysPage;
